import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { parseStoreAnalytics } from '../theme/storeAnalytics.js';
import { useStoreConsent } from '../hooks/useStoreConsent.js';
import {
  trackAddToCart,
  trackBeginCheckout,
  trackPageView,
  trackPurchase,
  trackViewItem,
} from '../tracking/storeTracking.js';

/**
 * @param {{
 *   slug: string,
 *   themeJson: unknown,
 *   productFocusId: number | null,
 *   drawerOpen: boolean,
 *   cartLines: Array<{ p: Record<string, unknown>, q: number, line: number }>,
 *   resolveProduct: (id: string | number) => Record<string, unknown> | null | undefined,
 *   onPurchase: (order: { orderId: string | number, total: number, items: Array<{ product_id?: number, id?: number, qty: number, unit?: number }> }) => void,
 * }} options
 */
export function useStoreTracking({
  slug,
  themeJson,
  productFocusId,
  drawerOpen,
  cartLines,
  resolveProduct,
}) {
  const location = useLocation();
  const consent = useStoreConsent(slug);
  const analytics = parseStoreAnalytics(themeJson);
  const analyticsOk = consent.decided && consent.analytics && Boolean(analytics.ga4MeasurementId);
  const marketingOk = consent.decided && consent.marketing && Boolean(analytics.metaPixelId);
  const trackingActive = analyticsOk || marketingOk;

  const lastPageRef = useRef('');
  const checkoutTrackedRef = useRef(false);
  const purchaseIdsRef = useRef(new Set());

  useEffect(() => {
    if (!trackingActive || !analyticsOk) return;
    const path = location.pathname + location.search;
    if (lastPageRef.current === path) return;
    lastPageRef.current = path;
    trackPageView(true, { page_path: path, page_title: document.title });
  }, [location.pathname, location.search, trackingActive, analyticsOk]);

  useEffect(() => {
    if (!trackingActive || productFocusId == null) return;
    const p = resolveProduct(productFocusId);
    if (p) trackViewItem(analyticsOk, marketingOk, p);
  }, [productFocusId, trackingActive, analyticsOk, marketingOk, resolveProduct]);

  useEffect(() => {
    if (!drawerOpen) {
      checkoutTrackedRef.current = false;
      return;
    }
    if (!trackingActive || checkoutTrackedRef.current || !cartLines.length) return;
    checkoutTrackedRef.current = true;
    const lines = cartLines.map(({ p, q }) => ({
      product_id: Number(p.id),
      qty: q,
      unit: Number(p.preco),
    }));
    trackBeginCheckout(analyticsOk, marketingOk, lines, resolveProduct);
  }, [drawerOpen, cartLines, trackingActive, analyticsOk, marketingOk, resolveProduct]);

  const notifyAddToCart = useCallback(
    (product, qty = 1) => {
      if (!trackingActive || !product) return;
      trackAddToCart(analyticsOk, marketingOk, product, qty);
    },
    [trackingActive, analyticsOk, marketingOk],
  );

  const notifyPurchase = useCallback(
    (order) => {
      if (!trackingActive) return;
      const txKey = String(order.orderId);
      if (purchaseIdsRef.current.has(txKey)) return;
      purchaseIdsRef.current.add(txKey);
      trackPurchase(analyticsOk, marketingOk, order, resolveProduct);
    },
    [trackingActive, analyticsOk, marketingOk, resolveProduct],
  );

  return { notifyAddToCart, notifyPurchase, consent, analytics };
}
